import React from 'react';
import { motion } from 'framer-motion';

import { Btn } from './Btn';
import { Confetti } from './Confetti';
import type { LadderRun } from '../../services/websocketService';

/**
 * THE RUN IS OVER.
 *
 * This screen has exactly one job: make you want to go again. So it is built
 * around ONE NUMBER — how deep you got — and one line under it, and a button.
 *
 * WHAT IS DELIBERATELY NOT HERE, and this is the whole design:
 *
 *   * No XP bar. No level. No coins, no gems, no energy, no streak-with-a-flame.
 *     The Ladder persists two things — best depth, and the `beat_atom_*` badges
 *     that already existed — and a fourth number would be a fourth reason to
 *     doubt the first one.
 *   * No leaderboard. A run moves no board, ever. Its reward is its own number,
 *     and that is what makes the wall a wall of skill rather than one of money.
 *   * No consolation. You lost. It says so. A run-over screen that congratulates
 *     you for dying is a run-over screen you stop believing.
 *
 * What IS here is the STORY: every rung, the letter, the Atom you faced, and the
 * two scores. Because the question a player actually has at this moment is "where
 * exactly did it go wrong", and the answer is a row in this table.
 */

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  expert: 'Expert',
  insane: 'Insane',
};

export interface LadderRunOverProps {
  run: LadderRun;
  onPlayAgain: () => void;
  onHome: () => void;
}

export function LadderRunOver({ run, onPlayAgain, onHome }: LadderRunOverProps) {
  const died = run.rungs.find(r => r.verdict === 'eliminated');

  return (
    <div className="app-shell">
      {/* The confetti fires for a NEW BEST and for nothing else. A celebration
          that fires every time is not a celebration, it is wallpaper. */}
      {run.isNewBest && <Confetti />}

      <div className="screen-pad wr-col gap-4" style={{ paddingTop: 24 }}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="wr-col gap-2"
          style={{ textAlign: 'center' }}
        >
          <div className="eyebrow accent" style={{ fontSize: 11 }}>
            {run.perfect ? 'YOU CLEARED THE LADDER' : run.isNewBest ? 'NEW BEST' : 'RUN OVER'}
          </div>

          {/* THE NUMBER. It is the biggest thing on the screen because it is the
              only thing on the screen that matters. */}
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.1 }}
            style={{
              fontFamily: 'var(--display)',
              fontWeight: 900,
              fontSize: 84,
              lineHeight: 1,
              color: run.isNewBest ? 'var(--accent)' : 'var(--ink-1)',
            }}
          >
            {run.depth}
          </motion.div>

          <div className="h3">You reached round {run.depth}.</div>

          <div className="wr-text-3" style={{ fontSize: 13 }}>
            {run.isNewBest
              ? run.previousBest > 0
                ? `Your best was ${run.previousBest}. Not any more.`
                : 'Your first run. The number goes up from here.'
              : `Best: ${run.bestDepth}.`}
          </div>

          {died && (
            <div className="wr-text-3" style={{ fontSize: 13, marginTop: 4 }}>
              {DIFFICULTY_LABEL[died.difficulty] || died.difficulty} Atom beat you on{' '}
              <strong style={{ color: 'var(--ink-1)' }}>{died.letter}</strong>, {died.atom}–{died.you}.
            </div>
          )}
        </motion.div>

        {/* THE CLIMB. Every rung, honestly, including the one that killed you. */}
        <div className="wr-col gap-1" style={{ marginTop: 8 }}>
          <div className="eyebrow">The climb</div>
          {run.rungs.map((r, i) => {
            const dead = r.verdict === 'eliminated';
            return (
              <motion.div
                key={r.round}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 + i * 0.07, duration: 0.25 }}
                className="prow"
                style={{
                  padding: '10px 12px',
                  background: dead
                    ? 'color-mix(in srgb, var(--coral, #b94a3b) 8%, var(--surface))'
                    : 'var(--surface)',
                  border: dead ? '1.5px solid var(--coral, #b94a3b)' : '1.5px solid transparent',
                }}
              >
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--display)',
                    fontWeight: 800,
                    fontSize: 14,
                    color: '#fff',
                    background: dead ? 'var(--coral, #b94a3b)' : 'var(--green, #1f8f4a)',
                  }}
                >
                  {r.letter}
                </span>

                <div className="body">
                  <div className="name" style={{ fontSize: 13 }}>
                    Round {r.round} · {DIFFICULTY_LABEL[r.difficulty] || r.difficulty} Atom
                  </div>
                  <div className="word" style={{ fontSize: 11 }}>
                    {r.bans.length > 0
                      ? `banned: ${r.bans.join(' · ')}`
                      : 'no bans'}
                  </div>
                </div>

                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 13,
                    fontWeight: 700,
                    color: dead ? 'var(--coral, #b94a3b)' : 'var(--ink-1)',
                  }}
                >
                  {r.you}–{r.atom}
                </span>
              </motion.div>
            );
          })}
        </div>

        <div style={{ flex: 1 }} />

        <div className="wr-col gap-2" style={{ paddingBottom: 12 }}>
          {/* The only button that matters. It says GO AGAIN, not "play again",
              because a run is a thing you go again at. */}
          <Btn kind="primary" size="xl" full icon="RotateCcw" onClick={onPlayAgain}>
            Go again
          </Btn>
          <Btn kind="ghost" full icon="House" onClick={onHome}>
            Home
          </Btn>
        </div>
      </div>
    </div>
  );
}
